<template>
  <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
    <BaseCard>
      <BaseCardHeader>
        <div class="flex justify-between items-baseline -mr-2">
          <h5 class="font-light">Web Presences</h5>
          <BaseIconButton v-if="!edit" icon="edit" brand @click="edit = !edit" />
          <template v-else>
            <div class="space-x-2 justify-end">
              <BaseIconButton icon="times" silver @click="doRevert" />
              <BaseIconButton
                green
                icon="check"
                :disabled="invalid"
                @click="handleSubmit(doSubmit)"
              />
            </div>
          </template>
        </div>
      </BaseCardHeader>
      <BaseCardBody>
        <form @submit.prevent>
          <div class="-m-4">
            <SimpleList nested :entities="localAccountWebPresences">
              <template #header>
                <ListingHeader />
              </template>
              <template #default="{ index, entity }">
                <ListingItem
                  :index="index"
                  :entity="entity"
                  :edit="edit"
                  :updating="updating"
                  @change="updateAccountWebPresence(index, $event)"
                  @destroy="removeAccountWebPresence(index)"
                />
              </template>
              <template #body-after>
                <BaseListItem v-if="localAccountWebPresences.length === 0">
                  <div class="flex items-baseline -mx-4">
                    <div class="flex-1 px-4 text-center">Nothing to see yet!</div>
                  </div>
                </BaseListItem>
                <BaseListItem v-if="edit && !updating">
                  <div class="flex -mx-2 justify-end">
                    <BaseIconButton icon="plus" green @click="addNewAccountWebPresence" />
                  </div>
                </BaseListItem>
              </template>
            </SimpleList>
          </div>
        </form>
      </BaseCardBody>
    </BaseCard>
  </ValidationObserver>
</template>

<script>
import SimpleList from '@/components/SimpleListFactory.vue';
import { entityComputed } from '@/store/helper';
import ListingItem from '@/components/AccountWebPresence/ListingItem.vue';
import ListingHeader from '@/components/AccountWebPresence/ListingHeader.vue';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const defaultEntity = {
  uuid: null,
  url: '',
  type: null,
};

export default {
  mixins: [veeValidateErrorsMixin],
  components: {
    SimpleList,
    ListingItem,
    ListingHeader,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      edit: false,
      updating: false,
      localAccountWebPresences: [],
    };
  },
  computed: {
    ...entityComputed,
    accountWebPresences() {
      const { webPresences: webPresenceIds = [] } = this.$currentAccount || {};
      return webPresenceIds.map((id) => this.entityById('accountWebPresences', id));
    },
  },
  watch: {
    accountWebPresences: {
      handler(newVal) {
        this.localAccountWebPresences = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },
  created() {
    this.localAccountWebPresences = JSON.parse(JSON.stringify(this.accountWebPresences));
  },
  methods: {
    doRevert() {
      this.localAccountWebPresences = JSON.parse(JSON.stringify(this.accountWebPresences));
      this.edit = false;
    },
    async doSubmit() {
      const { dispatch } = this.$store;
      const { accountId } = this;
      const accountUpdatedData = { webPresences: [...this.localAccountWebPresences] };

      this.updating = true;

      const dispatchWithErrorHandling = this.handleFormErrors(dispatch);

      await dispatchWithErrorHandling('ui/accounts/updateAccount', {
        accountId,
        accountUpdatedData,
      });

      this.updating = false;
    },
    addNewAccountWebPresence() {
      this.localAccountWebPresences.push({ ...defaultEntity });
    },
    removeAccountWebPresence(index) {
      this.localAccountWebPresences.splice(index, 1);
    },
    updateAccountWebPresence(index, webPresence) {
      this.localAccountWebPresences.splice(index, 1, webPresence);
    },
    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.edit = false;
          })
          .catch(({ status = null, data } = {}) => {
            if (status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            } else {
              this.errorMessage = 'Something went wrong! Please try again';
            }
          });
    },
  },
};
</script>
